import { CARD_VALUES } from './cardUtils';

const BOT_NAMES = ['Birdie', 'Bogey', 'Eagle', 'Mulligan', 'Caddy', 'Scratch', 'Putter', 'Wedge', 'Chipshot', 'Shank'];

function getBotNames(count) {
  return BOT_NAMES.slice(0, count);
}

function scoreCard(card) {
  return CARD_VALUES[card.rank] ?? 10;
}

function scoreHand(hand) {
  let total = 0;
  for (let col = 0; col < 3; col++) {
    const top = hand[col];
    const bottom = hand[col + 3];
    if (top && bottom && top.rank === bottom.rank) continue;
    if (top) total += scoreCard(top);
    if (bottom) total += scoreCard(bottom);
  }
  return total;
}

// Pick 2 cards to reveal at start (prefer low-value positions, but it's random-ish)
function chooseRevealPositions(hand) {
  // Just pick 2 random positions — bots don't know what's under them
  const positions = [0, 1, 2, 3, 4, 5];
  const shuffled = positions.sort(() => Math.random() - 0.5);
  return [shuffled[0], shuffled[1]];
}

// Decide whether to draw from stock or discard
function chooseDrawSource(hand, discardTop) {
  if (!discardTop) return 'stock';

  const discardValue = scoreCard(discardTop);

  // If discard is a King (0) or 2 (-2), almost always take it
  if (discardValue <= 0) return 'discard';

  // Check if discard would complete a column match
  for (let col = 0; col < 3; col++) {
    const top = hand[col];
    const bottom = hand[col + 3];

    if (top?.faceUp && !bottom?.faceUp && top.rank === discardTop.rank) return 'discard';
    if (bottom?.faceUp && !top?.faceUp && bottom.rank === discardTop.rank) return 'discard';
    if (top?.faceUp && bottom?.faceUp) {
      // Column already visible — would swapping improve it?
      if (discardValue < scoreCard(top)) return 'discard';
      if (discardValue < scoreCard(bottom)) return 'discard';
    }
  }

  // If discard is low (Ace=1, 3), take it if we have worse visible cards
  if (discardValue <= 3) {
    const worstVisible = getWorstVisiblePosition(hand);
    if (worstVisible !== null && scoreCard(hand[worstVisible]) > discardValue + 2) {
      return 'discard';
    }
  }

  return 'stock';
}

// Decide what to do with drawn card: swap (returns position) or discard (returns -1)
function chooseSwapOrDiscard(hand, drawnCard) {
  const drawnValue = scoreCard(drawnCard);

  // Check for column match opportunities first
  for (let col = 0; col < 3; col++) {
    const topIdx = col;
    const botIdx = col + 3;
    const top = hand[topIdx];
    const bottom = hand[botIdx];

    // If one card in column matches drawn card and other slot is available
    if (top?.faceUp && top.rank === drawnCard.rank && (!bottom?.faceUp || scoreCard(bottom) > 0)) {
      return botIdx;
    }
    if (bottom?.faceUp && bottom.rank === drawnCard.rank && (!top?.faceUp || scoreCard(top) > 0)) {
      return topIdx;
    }
  }

  // Find worst known card to potentially replace
  const worstPos = getWorstVisiblePosition(hand);
  if (worstPos !== null) {
    const worstValue = scoreCard(hand[worstPos]);
    if (drawnValue < worstValue - 1) {
      return worstPos;
    }
  }

  // If drawn card is good (<=3) and we have face-down cards, swap with a face-down card
  if (drawnValue <= 3) {
    const faceDownPositions = hand
      .map((c, i) => (!c.faceUp ? i : -1))
      .filter((i) => i >= 0);
    if (faceDownPositions.length > 0) {
      // Pick a face-down card that's not part of a potential match
      return faceDownPositions[Math.floor(Math.random() * faceDownPositions.length)];
    }
  }

  // If drawn card is medium (<=5) and we have face-down cards, sometimes swap
  if (drawnValue <= 5) {
    const faceDownPositions = hand
      .map((c, i) => (!c.faceUp ? i : -1))
      .filter((i) => i >= 0);
    if (faceDownPositions.length > 0 && Math.random() < 0.4) {
      return faceDownPositions[Math.floor(Math.random() * faceDownPositions.length)];
    }
  }

  // Discard
  return -1;
}

function getWorstVisiblePosition(hand) {
  let worstPos = null;
  let worstValue = -Infinity;

  for (let i = 0; i < 6; i++) {
    const card = hand[i];
    if (!card?.faceUp) continue;

    // Skip if this card is part of a column match
    const col = i % 3;
    const partner = i < 3 ? i + 3 : i - 3;
    if (hand[partner]?.faceUp && hand[partner].rank === card.rank) continue;

    const value = scoreCard(card);
    if (value > worstValue) {
      worstValue = value;
      worstPos = i;
    }
  }

  return worstPos;
}

export { getBotNames, chooseRevealPositions, chooseDrawSource, chooseSwapOrDiscard };
